import React, { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { Download, Save, FileText } from 'lucide-react';
import { fmtRange } from '@/lib/dates';
import HistoricalBillingWorkspace from './HistoricalBillingWorkspace';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v === null || v === undefined || v === '' ? '' : `$${Number(v).toFixed(2)}`);

const HistoricalBillingView = () => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState({ records: [], material_cost_total: 0, customer_billing_total: 0, count: 0, priced_count: 0 });
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});          // id -> string value
  const [saving, setSaving] = useState(null);
  const [filters, setFilters] = useState({ customer: '', branch: '', date: '', photo: '', billing: '' });
  const [exceptions, setExceptions] = useState([]);
  const [rejecting, setRejecting] = useState(null);      // wo id being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [workspaceId, setWorkspaceId] = useState(null);  // open HWO workspace slide-over

  const loadExceptions = () => {
    axios.get(`${API}/work-orders/historical/photo-exceptions`, authHeaders)
      .then((r) => setExceptions(r.data.records || [])).catch(() => setExceptions([]));
  };
  useEffect(() => { loadExceptions(); }, []);

  const decideException = async (id, decision, reason) => {
    try {
      await axios.post(`${API}/work-orders/historical/${id}/photo-exception/decision`, { decision, reason }, authHeaders);
      toast.success(`Exception ${decision === 'approve' ? 'approved' : 'rejected'}`);
      setRejecting(null); setRejectReason(''); loadExceptions(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record decision'); }
  };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/work-orders/historical/billing-review`, authHeaders)
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load report')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const customers = useMemo(() => [...new Set(data.records.map((r) => r.customer_name))].sort(), [data]);
  const rows = data.records.filter((r) =>
    (!filters.customer || r.customer_name === filters.customer) &&
    (!filters.branch || (r.service_location || '').toLowerCase().includes(filters.branch.toLowerCase())) &&
    (!filters.date || r.service_date === filters.date) &&
    (!filters.photo || r.documentation_status === filters.photo) &&
    (!filters.billing || r.billing_review_status === filters.billing));

  const saveAmount = async (r) => {
    const raw = edits[r.id];
    const amount = raw === '' || raw === undefined ? null : Number(raw);
    if (amount !== null && (isNaN(amount) || amount < 0)) { toast.error('Enter a valid amount or leave blank'); return; }
    setSaving(r.id);
    try {
      await axios.patch(`${API}/work-orders/historical/${r.id}/billing`, { customer_billing_amount: amount }, authHeaders);
      toast.success(`Billing amount ${amount === null ? 'cleared' : 'saved'} for ${r.tfs_work_order_number}`);
      setEdits((e) => { const n = { ...e }; delete n[r.id]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(null); }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/work-orders/historical/billing-review/export`, { ...authHeaders, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = 'historical_billing_review.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div data-testid="historical-billing-view">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">ROUT · Billing</div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0E2A47]">Historical Billing Review</h1>
          <p className="text-sm text-slate-500">Performed by Daro Cleaning · documentation & billing only — no payroll or payables.</p>
        </div>
        <button onClick={exportCsv} data-testid="hbr-export"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F]">
          <Download size={15} /> Export CSV
        </button>
      </div>

      {exceptions.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="hbr-photo-exceptions">
          <div className="mb-2 text-sm font-bold text-amber-800">Photo exceptions awaiting your approval ({exceptions.length})</div>
          <ul className="space-y-2">
            {exceptions.map((e) => (
              <li key={e.id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm" data-testid={`hbr-exc-${e.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-[#0E2A47]">{e.tfs_work_order_number}</span> · {e.service_location}
                    <div className="text-xs text-slate-500"><span className="capitalize">{e.exception_category}</span> unavailable — {e.exception_reason} · submitted by {e.submitted_by_name}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => decideException(e.id, 'approve')} data-testid={`hbr-exc-approve-${e.id}`}
                      className="rounded-full bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700">Approve</button>
                    <button onClick={() => { setRejecting(e.id); setRejectReason(''); }} data-testid={`hbr-exc-reject-${e.id}`}
                      className="rounded-full border border-red-300 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50">Reject</button>
                  </div>
                </div>
                {rejecting === e.id && (
                  <div className="mt-2 flex gap-2">
                    <input value={rejectReason} onChange={(ev) => setRejectReason(ev.target.value)} data-testid={`hbr-exc-reject-reason-${e.id}`}
                      placeholder="Reason (shown to Ana)" className="flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs" />
                    <button onClick={() => rejectReason.trim() ? decideException(e.id, 'reject', rejectReason.trim()) : toast.error('Enter a reason')}
                      data-testid={`hbr-exc-reject-confirm-${e.id}`} className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white">Confirm Reject</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-400">Records</div>
          <div className="text-xl font-bold text-[#0E2A47]" data-testid="hbr-count">{data.count}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-400">Material cost (internal)</div>
          <div className="text-xl font-bold text-[#0E2A47]" data-testid="hbr-material-total">{money(data.material_cost_total)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-400">Priced / total</div>
          <div className="text-xl font-bold text-[#0E2A47]">{data.priced_count}/{data.count}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-xs text-slate-400">Billing entered</div>
          <div className="text-xl font-bold text-[#0E2A47]">{money(data.customer_billing_total)}</div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <select value={filters.customer} onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
          data-testid="hbr-filter-customer" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">All customers</option>
          {customers.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input placeholder="Branch contains…" value={filters.branch} onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
          data-testid="hbr-filter-branch" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
        <input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          data-testid="hbr-filter-date" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
        <select value={filters.photo} onChange={(e) => setFilters({ ...filters, photo: e.target.value })}
          data-testid="hbr-filter-photo" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">All photo statuses</option>
          <option value="Photos Pending">Photos Pending</option>
          <option value="Photos Received">Photos Received</option>
        </select>
        <select value={filters.billing} onChange={(e) => setFilters({ ...filters, billing: e.target.value })}
          data-testid="hbr-filter-billing" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">All billing statuses</option>
          <option value="Pending Billing Review">Pending Billing Review</option>
          <option value="Approved for Invoicing">Approved for Invoicing</option>
        </select>
      </div>

      {loading ? <div className="p-6 text-slate-400">Loading…</div> : (
        <>
        <div className="hidden lg:block overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="p-2">WO #</th><th className="p-2">Customer</th><th className="p-2">Branch</th>
                <th className="p-2">Service date</th><th className="p-2">Work performed</th>
                <th className="p-2">Photos</th><th className="p-2">Billing status</th>
                <th className="p-2 text-right">Material</th><th className="p-2">Customer billing $</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top hover:bg-slate-50 cursor-pointer" data-testid={`hbr-row-${r.id}`} onClick={() => setWorkspaceId(r.id)}>
                  <td className="p-2 font-mono text-xs text-[#0E2A47] underline decoration-dotted" data-testid={`hbr-open-${r.id}`}>{r.tfs_work_order_number}</td>
                  <td className="p-2">{r.customer_name}</td>
                  <td className="p-2">{r.service_location}</td>
                  <td className="p-2 whitespace-nowrap">{fmtRange(r.service_date, r.service_date_end)}</td>
                  <td className="p-2 max-w-xs text-slate-600">{r.description_of_work}</td>
                  <td className="p-2 whitespace-nowrap text-xs">{r.documentation_status}<div className="text-slate-400">{r.before_photos}B / {r.after_photos}A</div></td>
                  <td className="p-2 text-xs">{r.billing_review_status}</td>
                  <td className="p-2 text-right whitespace-nowrap">{money(r.material_cost)}</td>
                  <td className="p-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400">$</span>
                      <input type="number" step="0.01" min="0" placeholder="blank"
                        data-testid={`hbr-amount-${r.id}`}
                        value={edits[r.id] !== undefined ? edits[r.id] : (r.customer_billing_amount ?? '')}
                        onChange={(e) => setEdits({ ...edits, [r.id]: e.target.value })}
                        className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm" />
                    </div>
                  </td>
                  <td className="p-2" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => saveAmount(r)} disabled={saving === r.id || edits[r.id] === undefined}
                      data-testid={`hbr-save-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-[#5FA718] px-2 py-1 text-xs font-semibold text-white hover:bg-[#4e8c14] disabled:opacity-40">
                      <Save size={12} /> Save
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={10} className="p-6 text-center text-slate-400"><FileText size={20} className="mx-auto mb-1" />No records match the filters.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile / tablet: compact historical billing cards with inline amount entry */}
        <MobileRecordList className="lg:hidden" testid="hbr-mobile-list">
          {rows.map((r) => (
            <MobileRecordCard
              key={r.id}
              testid={`hbr-card-${r.id}`}
              onClick={() => setWorkspaceId(r.id)}
              title={r.tfs_work_order_number}
              onTitleClick={() => setWorkspaceId(r.id)}
              titleTestid={`hbr-open-m-${r.id}`}
              badge={<span className="whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{r.billing_review_status}</span>}
              meta={[
                { label: 'Customer', value: r.customer_name, full: true },
                { label: 'Branch', value: r.service_location, full: true },
                { label: 'Service date', value: fmtRange(r.service_date, r.service_date_end) },
                { label: 'Photos', value: `${r.before_photos}B / ${r.after_photos}A` },
                { label: 'Work performed', value: r.description_of_work, full: true },
                { label: 'Material', value: money(r.material_cost) || '—' },
              ]}
            >
              <div className="mt-2 flex items-end gap-2 border-t border-slate-100 pt-2" onClick={(e) => e.stopPropagation()}>
                <div className="flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Customer billing $</div>
                  <div className="mt-0.5 flex items-center gap-1">
                    <span className="text-slate-400">$</span>
                    <input type="number" step="0.01" min="0" placeholder="blank"
                      data-testid={`hbr-amount-m-${r.id}`}
                      value={edits[r.id] !== undefined ? edits[r.id] : (r.customer_billing_amount ?? '')}
                      onChange={(e) => setEdits({ ...edits, [r.id]: e.target.value })}
                      className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm" />
                  </div>
                </div>
                <button onClick={() => saveAmount(r)} disabled={saving === r.id || edits[r.id] === undefined}
                  data-testid={`hbr-save-m-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-[#5FA718] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#4e8c14] disabled:opacity-40">
                  <Save size={12} /> Save
                </button>
              </div>
            </MobileRecordCard>
          ))}
          {!rows.length && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-400">
              <FileText size={20} className="mx-auto mb-1" />No records match the filters.
            </div>
          )}
        </MobileRecordList>
        </>
      )}
      <p className="mt-2 text-xs text-slate-400">Blank customer billing amounts are exported as blank (never $0). Click a work-order number to open its full billing workspace (cost breakdown, pricing, profitability, workflow).</p>
      {workspaceId && (
        <HistoricalBillingWorkspace workOrderId={workspaceId}
          onClose={() => setWorkspaceId(null)}
          onSaved={() => load()} />
      )}
    </div>
  );
};

export default HistoricalBillingView;
